import React from 'react'
import { venue } from 'app/stlswing/content/classes'

/** A fact about the class: label on the left, value on the right */
export const Detail = ({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) => (
  <div className='py-4 border-t border-rule sm:grid sm:grid-cols-3 sm:gap-4'>
    <dt className='font-bold'>{label}</dt>
    <dd className='mt-1 text-muted sm:mt-0 sm:col-span-2'>{children}</dd>
  </div>
)

export const LocationDetail = () => (
  <Detail label='Location'>
    <p className='font-bold text-ink'>{venue.name}</p>
    <p>{venue.address}</p>
    <p className='mt-2'>{venue.description}</p>
    <a href={venue.mapUrl} target='_blank' rel='noreferrer' className='link'>
      Open in Maps
    </a>
  </Detail>
)

const Tick = () => (
  <svg
    className='flex-shrink-0 w-5 h-5 mt-1 mr-3 text-spot'
    viewBox='0 0 20 20'
    fill='currentColor'
    aria-hidden='true'>
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 111.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z'
    />
  </svg>
)

/** A list of things you get. Only for real facts, not for jokes */
export const TickList = ({
  items,
  onDark,
}: {
  items: string[]
  onDark?: boolean
}) => (
  <ul className='space-y-2'>
    {items.map((item) => (
      <li key={item} className='flex items-start'>
        {onDark ? (
          <Tick />
        ) : (
          <span className='flex-shrink-0 w-5 h-5 mt-1 mr-3 rounded-full bg-ink'>
            <svg
              className='w-5 h-5 p-1 text-spot'
              viewBox='0 0 20 20'
              fill='currentColor'
              aria-hidden='true'>
              <path
                fillRule='evenodd'
                clipRule='evenodd'
                d='M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 111.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z'
              />
            </svg>
          </span>
        )}
        <span>{item}</span>
      </li>
    ))}
  </ul>
)

type Step = { time: string; title: string; description: string }

/** What happens over the course of the night */
export const NightSchedule = ({ steps }: { steps: Step[] }) => (
  <ol>
    {steps.map((step, i) => (
      <li key={step.time} className='relative pb-8 pl-9 last:pb-0'>
        {i < steps.length - 1 && (
          <span
            className='absolute top-4 bottom-0 w-0.5 bg-ink left-[7px]'
            aria-hidden='true'
          />
        )}
        <span
          className='absolute left-0 w-4 h-4 border-2 rounded-full bg-spot border-ink top-1.5'
          aria-hidden='true'
        />
        <p className='kicker'>{step.time}</p>
        <p className='mt-1 h3'>{step.title}</p>
        <p className='mt-1 text-muted'>{step.description}</p>
      </li>
    ))}
  </ol>
)

type SignupCardProps = {
  /** Shown first, e.g. the start date */
  heading: string
  price: string
  priceNote: string
  facts: string[]
  href: string
  buttonText: string
  footnote?: React.ReactNode
}

/** Date, price, key facts and the sign up button. Stays beside the page content on desktop */
export const SignupCard = ({
  heading,
  price,
  priceNote,
  facts,
  href,
  buttonText,
  footnote,
}: SignupCardProps) => (
  <aside className='p-6 text-white shadow-xl bg-night-glow rounded-3xl md:p-8 lg:sticky lg:top-24'>
    <p className='kicker !text-spot'>{heading}</p>
    <p className='flex flex-wrap items-baseline mt-2 gap-x-3 gap-y-1'>
      <span className='text-4xl leading-none sm:text-5xl font-display font-black'>
        {price}
      </span>
      <span className='text-muted-dark'>{priceNote}</span>
    </p>
    <div className='pt-5 mt-5 border-t border-white/20'>
      <TickList items={facts} onDark />
    </div>
    <a
      href={href}
      data-testid='class-signup-button'
      className='w-full mt-6 btn btn-primary'>
      {buttonText}
    </a>
    {footnote && <p className='mt-4 text-sm text-muted-dark'>{footnote}</p>}
  </aside>
)
